import {ActionIcon, Button, Paper, TextInput, Select, Tooltip} from "@mantine/core";
import {DataTable, type DataTableSortStatus} from "mantine-datatable";
import {ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Link, useNavigate} from "react-router";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Row, Col} from "../../components/Flex/Flex";
import Page from "../../components/Page/Page.tsx";
import MessageContext from "../../context/MessageContext.tsx";
import CGT from "../../utils/CGT.ts";
import {calculateTime, calculateTimeSplit, formatDurationShort} from "../../utils/hyper.ts";
import Premium from "../../utils/Premium.ts";
import AddRoute, {SelectSystem} from "./AddRoute.tsx";
import api, {useEntityTypes, useUser} from '../../utils/api';
import BonusChip from "./BonusChip.tsx";
import NumberSelector from "./NumberSelector.tsx";
import { WarningCircleIcon, BookmarksIcon, ArrowClockwiseIcon, MagnifyingGlassIcon, TrashIcon } from "@phosphor-icons/react";
import {HyperlanePreset, Route, RouteGroup} from "./types.ts";
import PresetModal from "./PresetModal.tsx";
import useScreenSize from "../../components/useScreenSize.tsx";
import SearchSelect from "../../components/SearchSelect/SearchSelect.tsx";
import useDataTableSessionState from "../../hooks/useDataTableSessionState.ts";
import usePresets, {usePresetsMutations} from "./usePresets.ts";
import axios from "axios";
import {track} from "../../utils/analytics.ts";


const bodySystem = (row: RouteGroup, config: { field: string }) => {
  if (config.field === 'start' || config.field === 'end') {
    let s = row[config.field];
    return <div>{s.name}<br/><small>({s.galX}, {s.galY})</small></div>
  }
  return '';
}


const bodyRoutes = (row: RouteGroup) => {
  const {min, max} = row.routes.reduce((acc, r) => {
    const v = r.path.length - 1;
    if (v > acc.max) {
      acc.max = v;
    }
    if (v < acc.min) {
      acc.min = v;
    }

    return acc;
  }, {min: Infinity, max: -Infinity})
  return `${min} - ${max} (${row.routes.length})`;
}
const lastUpdate = (row: RouteGroup) => {
  let date = CGT.fromSeconds(row.lastUpdate || 0);
  let now = CGT.now();
  let className = "";
  if (date < now.minus({day: 15})) {
    className = 'text-red';
  } else if (date < now.minus({day: 7})) {
    className = 'text-yellow'
  }

  return (
    <div>
      <span>{date.toCGT()}</span><br/>
      <small className={className}>{date.toRelative()}</small>
    </div>
  )
}

function RouteList() {
  const {success, error, setLoaderMessage} = useContext(MessageContext);
  const {user} = useUser()
  const {ships} = useEntityTypes(['ships'], true);
  const navigate = useNavigate();
  const {presets, defaultPresetId, isSuccess: presetsLoaded} = usePresets();
  const presetMutations = usePresetsMutations();
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [skill, setSkill] = useState<number | undefined>(0);
  const [hyper, setHyper] = useState<number | undefined>(1);
  const [sublight, setSublight] = useState<number | undefined>(10);
  const [selectedShipId, setSelectedShipId] = useState<string | null>(null);
  const [presetModalOpen, setPresetModalOpen] = useState<boolean>(false);
  const [routes, setRoutes] = useState<RouteGroup[]>([]);
  const presetDefaultsApplied = useRef<boolean>(false);
  const {sortStatus, setSortStatus, filters, setFilter} = useDataTableSessionState<
    DataTableSortStatus<RouteGroup>,
    {globalFilter: string}
  >({
    storageKey: 'hyper-routes:list',
    initialSortStatus: {
      columnAccessor: 'name',
      direction: 'asc',
    },
    initialFilters: {
      globalFilter: '',
    },
  });
  const globalFilter = typeof filters.globalFilter === 'string' ? filters.globalFilter : '';

  const {isMedium} = useScreenSize()

  useEffect(() => {
    api.get('/hyper/v2/routes').then(res => {
      setRoutes(res.data.routes);
    })
  }, []);

  useEffect(() => {
    if (presetDefaultsApplied.current || !presetsLoaded) {
      return;
    }
    presetDefaultsApplied.current = true;

    if (!defaultPresetId) {
      return;
    }
    const preset = presets.find(p => p.id === defaultPresetId);
    if (preset) {
      setSelectedPresetId(preset.id);
      setSkill(preset.skill);
      setHyper(preset.hyper);
      setSublight(preset.sublight);
    }
  }, [presetsLoaded, defaultPresetId, presets]);

  const filteredRecords = useMemo(() => {
    if (!globalFilter) return routes;
    const lower = globalFilter.toLowerCase();
    return routes.filter(r =>
      r.name?.toLowerCase().includes(lower) ||
      r.start?.name?.toLowerCase().includes(lower) ||
      r.end?.name?.toLowerCase().includes(lower)
    );
  }, [routes, globalFilter]);

  const sortedRecords = useMemo(() => {
    const sorted = [...filteredRecords].sort((a, b) => {
      const accessor = sortStatus.columnAccessor;
      const aVal = accessor.includes('.') ? accessor.split('.').reduce((o: any, k) => o?.[k], a) : a[accessor as keyof RouteGroup];
      const bVal = accessor.includes('.') ? accessor.split('.').reduce((o: any, k) => o?.[k], b) : b[accessor as keyof RouteGroup];
      if (typeof aVal === 'string') return aVal.localeCompare((bVal as string) ?? '');
      return ((aVal as number) ?? 0) - ((bVal as number) ?? 0);
    });
    return sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
  }, [filteredRecords, sortStatus]);

  const getRouteBaseTotals = useCallback((route: Route) => {
    return route.path.reduce((acc, segment, index) => {
      if (index === 0) {
        return acc;
      }
      const hyperBase = segment.hyperCost ?? segment.cost ?? 0;
      const subBase = segment.sublightCost ?? 0;
      return {
        hyperBase: acc.hyperBase + hyperBase,
        sublightBase: acc.sublightBase + subBase,
        totalBase: acc.totalBase + (segment.cost ?? 0),
      };
    }, {hyperBase: 0, sublightBase: 0, totalBase: 0});
  }, []);

  const getBestRoute = useCallback((row: RouteGroup) => {
    return row.routes.reduce((best, r) => best === null || r.cost < best.cost ? r : best, null as Route | null);
  }, []);

  const bodyTime = useCallback((row: RouteGroup) => {
    const applyHyper = hyper || 1;
    const applySublight = sublight || 1;
    const applySkill = skill || 0;
    const bestRoute = getBestRoute(row);
    let d;
    const isV2 = (row.version ?? 1) >= 2;
    if (bestRoute && isV2) {
      const totals = getRouteBaseTotals(bestRoute);
      d = calculateTimeSplit(totals.hyperBase, totals.sublightBase, applyHyper, applySublight, applySkill);
    } else {
      d = calculateTime(row.bestCost, applyHyper, applySkill);
    }
    let warn = null;
    if (row.exclude.length > 0 || row.avoidSpacePvp) {
      warn = <Tooltip label={row.avoidSpacePvp ? "This route avoids space PvP systems" + (row.exclude.length > 0 ? " and excludes some systems" : "") : "This route excludes some systems"}><WarningCircleIcon size={16} className="text-yellow-600" /></Tooltip>;
    }

    if (row.baseCost) {
      let d2 = isV2
        ? calculateTimeSplit(row.baseCost, 0, applyHyper, applySublight, applySkill)
        : calculateTime(row.baseCost, applyHyper, applySkill);
      let diff = Math.round((d.as('seconds') - d2.as('seconds')) * 100 / d2.as('seconds'));
      return <Row align="center"><Col>{warn}</Col><Col grow={1} className="text-right">{isMedium ? d.toHuman() : formatDurationShort(d)}</Col><Col><BonusChip value={diff}/></Col></Row>
    } else {
      return <Row align="center"><Col grow={1}>{isMedium ? d.toHuman() : formatDurationShort(d)}</Col></Row>
    }
  }, [skill, hyper, sublight, getBestRoute, getRouteBaseTotals]);


  const onSave = useCallback(async (start: SelectSystem, end: SelectSystem, name: string, exclude: string[], avoidSpacePvp: boolean) => {
    setLoaderMessage('Generating hyper route');
    try {
      const res = await api.post('/hyper/v2/route', {start, end, name, exclude, avoidSpacePvp});
      track('hl:route-calculate', {
        fromSystem: start.id !== '',
        toSystem: end.id !== '',
        excludes: exclude.length,
        avoidPvp: avoidSpacePvp,
        named: name.trim() !== '',
      });
      setRoutes(prev => [...prev, res.data.route]);
      navigate(`/hl/routes/${res.data.route.id}`);
    } catch (e) {
      track('hl:route-calculate-failed', {status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0});
      throw e;
    } finally {
      setLoaderMessage('');
    }
  }, [navigate, setLoaderMessage]);

  const updateRoute = useCallback((updated: RouteGroup) => {
    setRoutes(prev => {
      let next = [...prev];
      let i = next.findIndex(r => r.id === updated.id);
      next[i] = updated;
      return next;
    })
  }, []);


  const bodyActions = useCallback((row: RouteGroup) => {
    return (
      <Row>
        <ActionIcon variant="light" radius="xl" onClick={(e) => {
          e.stopPropagation();
          setLoaderMessage('Recalculating hyper route');
          api.post('/hyper/v2/route/' + row.id + '/refresh').then(res => {
            track('hl:route-refresh', {from: 'list'});
            updateRoute(res.data);
            success('Refresh successful', 'The hyper route has been recalculated');
          }).catch(() => {
            error('Refresh failed', 'The hyper route could not be recalculated');
          }).finally(() => setLoaderMessage(''));
        }} title="Recalculate route">
          <ArrowClockwiseIcon size={16}/>
        </ActionIcon>
        <ActionIcon variant="light" onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          api.delete('/hyper/v2/route/' + row.id).then(() => {
            track('hl:route-delete');
            success('Hyper route removed', 'The route has been removed');
            setRoutes(prev => prev.filter(r => r.id !== row.id));
          })
        }} title="Delete route">
          <TrashIcon size={16}/>
        </ActionIcon>
      </Row>
    )
  }, []);

  const presetOptions = useMemo(() => presets.map(p => ({value: p.id, label: p.name})), [presets]);

  const shipOptions = useMemo(() => ships.filter(s => s.speed.hyper > 0).map(s => ({value: s.id, label: s.name})), [ships]);

  const applyPreset = useCallback((preset: HyperlanePreset) => {
    setSelectedPresetId(preset.id);
    setSkill(preset.skill);
    setHyper(preset.hyper);
    setSublight(preset.sublight);
  }, []);

  const setDefaultPreset = useCallback((id: string) => {
    api.post(`/hyper/v2/preset/${id}/default`).then(res => {
      track('hl:preset-default');
      presetMutations.setDefaultPreset(res.data.defaultPresetId ?? id);
    })
  }, [presetMutations]);

  const onSelectPreset = useCallback((id: string | null) => {
    if (!id) {
      setSelectedPresetId(null);
      setSkill(0);
      setHyper(1);
      setSublight(1);
      setSelectedShipId(null);
      return;
    }
    const preset = presets.find(p => p.id === id);
    if (preset) {
      applyPreset(preset);
      track('hl:preset-select', {from: 'list'});
    }
  }, [applyPreset, presets]);

  const createPreset = useCallback((payload: { name: string; skill: number; hyper: number; sublight: number }) => {
    api.post('/hyper/v2/preset', payload).then(res => {
      const created = res.data.preset as HyperlanePreset;
      presetMutations.addPreset(created);
      track('hl:preset-create');
      success('Preset created', 'Your hyperlane preset has been created');
    });
  }, [presetMutations, success]);

  const removePreset = useCallback((id: string) => {
    api.delete(`/hyper/v2/preset/${id}`).then(() => {
      presetMutations.removePreset(id);
      track('hl:preset-delete');
      if (selectedPresetId === id) {
        setSelectedPresetId(null);
      }
      success('Preset removed', 'The preset has been deleted');
    });
  }, [presetMutations, selectedPresetId, success]);

  const onFilter = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFilter('globalFilter', e.target.value ?? '');
  }, [setFilter]);

  return (
    <Page>
      <title>Hyper Routes</title>
      {user.subLevel === 0 ?
        <h2>Calculated Routes ({routes.length}/{Premium.HYPER_ROUTES})</h2> :
        <h2>Calculated Routes ({routes.length})</h2>}

      <Paper className="mt-2">
        <Row align="end" mb={2}>
          <Col>
            <Select
              label="Preset"
              value={selectedPresetId}
              data={presetOptions}
              onChange={onSelectPreset}
              placeholder="Select preset"
            />
          </Col>
          <Col>
            {isMedium ? (<>
              <Button onClick={() => setPresetModalOpen(true)} leftSection={<BookmarksIcon />}>Manage presets</Button>
            </>) : (
              <ActionIcon onClick={() => setPresetModalOpen(true)}>
                <BookmarksIcon />
              </ActionIcon>
            )}
          </Col>
          <Col>
            <SearchSelect
              value={selectedShipId}
              data={shipOptions}
              label="Ship"
              onChange={(id) => {
                setSelectedShipId(id);
                const ship = ships.find(s => s.id === id);
                if (ship) {
                  setHyper(ship.speed.hyper || 1);
                  setSublight(ship.speed.sublight || 1);
                  track('hl:ship-select');
                }
              }}
              placeholder="Load ship stats"
            />
          </Col>
          <Col>
            <NumberSelector label="Pilot Skill" min={0} max={5} value={skill} onChange={next => setSkill(next)}/>
          </Col>
          <Col>
            <NumberSelector label="Hyper" min={1} max={15} value={hyper} onChange={next => setHyper(next)}/>
          </Col>
          <Col>
            <NumberSelector label="Sublight" min={0} max={300} step={5} value={sublight} onChange={next => setSublight(next.value)}/>
          </Col>
          <Col grow={1}/>
          <Col>
            <TextInput
              leftSection={<MagnifyingGlassIcon size={16}/>}
              value={globalFilter}
              onChange={onFilter}
              placeholder="Search"
            />
          </Col>
        </Row>
        <DataTable
          records={sortedRecords}
          idAccessor="id"
          onRowClick={({record}) => navigate(`/hl/routes/${record.id}`)}
          sortStatus={sortStatus}
          onSortStatusChange={setSortStatus}
          columns={[
            {accessor: 'name', title: 'Name', sortable: true},
            {accessor: 'start', title: 'Start', sortable: true, sortKey: 'start.name', render: (row) => bodySystem(row, {field: 'start'})},
            {accessor: 'end', title: 'End', sortable: true, sortKey: 'end.name', render: (row) => bodySystem(row, {field: 'end'})},
            {accessor: 'routes', title: 'Jumps (routes)', render: bodyRoutes},
            {accessor: 'bestCost', title: 'Time', sortable: true, render: bodyTime},
            {accessor: 'lastUpdate', title: 'Last update', sortable: true, render: lastUpdate},
            {accessor: 'actions', title: 'Actions', render: bodyActions},
          ]}
        />
      </Paper>

      {user.subLevel === 0 && routes.length >= Premium.HYPER_ROUTES ? <RouteLimit/> : <AddRoute onSave={onSave}/>}

      <PresetModal
        opened={presetModalOpen}
        onClose={() => setPresetModalOpen(false)}
        presets={presets}
        defaultPresetId={defaultPresetId}
        ships={ships}
        onCreate={createPreset}
        onRemove={removePreset}
        onSetDefault={setDefaultPreset}
      />

    </Page>
  )
}

const RouteLimit = () => {
  return (
    <Paper className="mt-2">
      You have reached the limit of available routes for free users. <Link to="/subscribe">Subscribe now</Link> to get unlimited hyper routes!
    </Paper>
  )
}


export default withRouteErrorBoundary(RouteList);
